import { useState, useEffect, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import {
    adminGetVehicles, adminUpdateVehicle, adminGetSettings, adminUpdateSetting,
    adminGetLeads, adminGetSyncLogs, adminTriggerSync, adminGetStats, adminGetSyncStatus,
    adminCreateVehicle, adminDeleteVehicle, adminChangePassword,
    adminGetSyncConfig, adminTestSyncConfig, adminDiagnoseScraping
} from '@/lib/api';
import { formatPrice, getStatusBadge } from '@/lib/utils';
import { ImageUploader } from '@/components/ImageUploader';
import { toast } from 'sonner';
import {
    RefreshCw, LogOut, Car, Users, Settings, FileText,
    Eye, CheckCircle, MessageCircle, Plus, Pencil, Trash2,
    Phone, Mail, Lock, Loader2, X, Save, Cloud, AlertTriangle
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function AdminDashboardPage() {
    const navigate = useNavigate();
    const { isAuthenticated, logout, user } = useAuth();
    const [activeTab, setActiveTab] = useState('inventory');
    const [vehicles, setVehicles] = useState([]);
    const [leads, setLeads] = useState([]);
    const [syncLogs, setSyncLogs] = useState([]);
    const [settings, setSettings] = useState({});
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);

    // Edit vehicle dialog
    const [editVehicle, setEditVehicle] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [savingEdit, setSavingEdit] = useState(false);

    // Create vehicle dialog
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({ title: '', brand: '', model: '', price: '', year: '', km: '', fuel: '', transmission: '', description: '', images: [], status: 'available' });
    const [savingCreate, setSavingCreate] = useState(false);

    // Change password
    const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
    const [savingPw, setSavingPw] = useState(false);

    // Sync (scraping remoto) config
    const [syncConfig, setSyncConfig] = useState(null);
    const [syncForm, setSyncForm] = useState({ emergent_url: '', remote_sync_key: '' });
    const [testingSync, setTestingSync] = useState(false);
    const [syncTestResult, setSyncTestResult] = useState(null);
    const [diagnosing, setDiagnosing] = useState(false);

    const handleDiagnose = async () => {
        setDiagnosing(true);
        try {
            const r = await adminDiagnoseScraping();
            if (r.data.ok) toast.success(`coches.net responde desde este servidor: ${r.data.items_found} coches detectados.`);
            else toast.error('coches.net bloquea o no responde desde este servidor.');
            await loadSyncConfig();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'No se pudo completar el diagnóstico.');
        } finally {
            setDiagnosing(false);
        }
    };

    const handleChangeStrategy = async (value) => {
        try {
            await adminUpdateSetting('sync_strategy', value);
            setSyncConfig(p => ({ ...p, strategy: value }));
            toast.success('Estrategia de sincronización actualizada.');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al cambiar la estrategia.');
        }
    };

    const loadSyncConfig = useCallback(async () => {
        try {
            const r = await adminGetSyncConfig();
            setSyncConfig(r.data);
            setSyncForm(p => ({ ...p, emergent_url: r.data.emergent_url || '' }));
        } catch (e) {}
    }, []);

    const handleSaveSyncConfig = async () => {
        setSavingSettings(true);
        try {
            await adminUpdateSetting('emergent_url', syncForm.emergent_url.trim());
            if (syncForm.remote_sync_key.trim()) {
                await adminUpdateSetting('remote_sync_key', syncForm.remote_sync_key.trim());
            }
            toast.success('Configuración de sincronización guardada.');
            setSyncForm(p => ({ ...p, remote_sync_key: '' }));
            await loadSyncConfig();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar la configuración.');
        } finally {
            setSavingSettings(false);
        }
    };

    const handleTestSyncConfig = async () => {
        setTestingSync(true);
        setSyncTestResult(null);
        try {
            const r = await adminTestSyncConfig();
            setSyncTestResult(r.data);
            if (r.data.ok) toast.success(`Conexión OK: ${r.data.count} coches disponibles.`);
            else toast.error(r.data.error || 'Fallo de conexión.');
        } catch (err) {
            setSyncTestResult({ ok: false, error: err.response?.data?.detail || 'Error de red.' });
            toast.error('Fallo al probar la conexión.');
        } finally {
            setTestingSync(false);
        }
    };

    useEffect(() => {
        if (!isAuthenticated) navigate('/admin');
    }, [isAuthenticated, navigate]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [vRes, lRes, sRes, stRes, logRes] = await Promise.all([
                adminGetVehicles().catch((e) => ({ _err: e, data: { vehicles: [] } })),
                adminGetLeads().catch((e) => ({ _err: e, data: { leads: [] } })),
                adminGetSettings().catch((e) => ({ _err: e, data: {} })),
                adminGetStats().catch((e) => ({ _err: e, data: {} })),
                adminGetSyncLogs().catch((e) => ({ _err: e, data: { logs: [] } })),
            ]);

            // Si alguna llamada devuelve 401/403, la sesión ha expirado → volver a login
            const authErr = [vRes, lRes, sRes, stRes, logRes].find(r => {
                const s = r?._err?.response?.status;
                return s === 401 || s === 403;
            });
            if (authErr) {
                toast.error('Sesión expirada. Vuelve a iniciar sesión.');
                localStorage.removeItem('admin_token');
                navigate('/admin');
                return;
            }

            setVehicles(vRes.data.vehicles || []);
            setLeads(lRes.data.leads || []);
            setSettings(sRes.data || {});
            setStats(stRes.data || {});
            setSyncLogs(logRes.data.logs || []);
        } catch (err) {
            toast.error('Error al cargar datos.');
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        if (isAuthenticated) { fetchData(); loadSyncConfig(); }
    }, [isAuthenticated, fetchData, loadSyncConfig]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            const r = await adminTriggerSync();
            toast.info(r.data.message || 'Sincronización iniciada...');
            const poll = setInterval(async () => {
                try {
                    const r = await adminGetSyncStatus();
                    if (!r.data.in_progress) {
                        clearInterval(poll);
                        setSyncing(false);
                        toast.success('Sincronización completada.');
                        fetchData();
                    }
                } catch (e) {}
            }, 5000);
            setTimeout(() => { clearInterval(poll); setSyncing(false); fetchData(); }, 900000);
        } catch (err) {
            setSyncing(false);
            toast.error(err.response?.data?.detail || 'Error al sincronizar.');
        }
    };

    // ---- EDIT VEHICLE ----
    const openEdit = (v) => {
        setEditVehicle(v);
        setEditForm({
            title: v.custom_title || v.title || '',
            price: v.custom_price || v.price || '',
            description: v.custom_description || v.description || '',
            status: v.status || 'available',
            featured: v.featured || false,
            year: v.year || '',
            km: v.km || '',
            fuel: v.fuel || '',
            transmission: v.transmission || '',
            horsepower: v.horsepower || '',
            images: v.images || [],
        });
    };

    const saveEdit = async () => {
        if (!editVehicle) return;
        setSavingEdit(true);
        try {
            const updates = {};
            if (editForm.title !== (editVehicle.custom_title || editVehicle.title)) updates.custom_title = editForm.title;
            if (Number(editForm.price) !== (editVehicle.custom_price || editVehicle.price)) updates.custom_price = Number(editForm.price) || null;
            if (editForm.description !== (editVehicle.custom_description || editVehicle.description)) updates.custom_description = editForm.description;
            if (editForm.status !== editVehicle.status) updates.status = editForm.status;
            if (editForm.featured !== editVehicle.featured) updates.featured = editForm.featured;
            if (Number(editForm.year) !== editVehicle.year) updates.year = Number(editForm.year) || null;
            if (Number(editForm.km) !== editVehicle.km) updates.km = Number(editForm.km) || null;
            if (editForm.fuel !== editVehicle.fuel) updates.fuel = editForm.fuel;
            if (editForm.transmission !== editVehicle.transmission) updates.transmission = editForm.transmission;
            if (Number(editForm.horsepower) !== editVehicle.horsepower) updates.horsepower = Number(editForm.horsepower) || null;
            // Check if images changed
            const oldImgs = JSON.stringify(editVehicle.images || []);
            const newImgs = JSON.stringify(editForm.images || []);
            if (oldImgs !== newImgs) updates.images = editForm.images;

            if (Object.keys(updates).length === 0) {
                toast.info('No hay cambios.');
                setEditVehicle(null);
                return;
            }
            await adminUpdateVehicle(editVehicle.id, updates);
            toast.success('Vehículo actualizado.');
            setEditVehicle(null);
            fetchData();
        } catch (err) {
            toast.error('Error al guardar.');
        } finally {
            setSavingEdit(false);
        }
    };

    // ---- CREATE VEHICLE ----
    const saveCreate = async () => {
        if (!createForm.title) { toast.error('El título es obligatorio.'); return; }
        setSavingCreate(true);
        try {
            await adminCreateVehicle({
                title: createForm.title,
                brand: createForm.brand || createForm.title.split(' ')[0],
                model: createForm.model || createForm.title.split(' ').slice(1).join(' '),
                price: Number(createForm.price) || null,
                year: Number(createForm.year) || null,
                km: Number(createForm.km) || null,
                fuel: createForm.fuel,
                transmission: createForm.transmission,
                description: createForm.description,
                images: createForm.images || [],
                status: createForm.status,
            });
            toast.success('Vehículo creado.');
            setShowCreate(false);
            setCreateForm({ title: '', brand: '', model: '', price: '', year: '', km: '', fuel: '', transmission: '', description: '', images: [], status: 'available' });
            fetchData();
        } catch (err) {
            toast.error('Error al crear vehículo.');
        } finally {
            setSavingCreate(false);
        }
    };

    // ---- DELETE VEHICLE ----
    const handleDelete = async (v) => {
        if (!window.confirm(`¿Eliminar "${v.title}"?`)) return;
        try {
            await adminDeleteVehicle(v.id);
            toast.success('Vehículo eliminado.');
            fetchData();
        } catch (err) {
            toast.error('Error al eliminar.');
        }
    };

    // ---- CHANGE PASSWORD ----
    const handleChangePw = async () => {
        if (!pwForm.current_password || !pwForm.new_password) { toast.error('Rellena todos los campos.'); return; }
        if (pwForm.new_password !== pwForm.confirm) { toast.error('Las contraseñas no coinciden.'); return; }
        if (pwForm.new_password.length < 6) { toast.error('Mínimo 6 caracteres.'); return; }
        setSavingPw(true);
        try {
            await adminChangePassword({ current_password: pwForm.current_password, new_password: pwForm.new_password });
            toast.success('Contraseña cambiada correctamente.');
            setPwForm({ current_password: '', new_password: '', confirm: '' });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al cambiar contraseña.');
        } finally {
            setSavingPw(false);
        }
    };

    const handleSaveSetting = async (key, value) => {
        setSavingSettings(true);
        try {
            await adminUpdateSetting(key, value);
            setSettings(prev => ({ ...prev, [key]: value }));
            toast.success('Guardado.');
        } catch (err) {
            toast.error('Error al guardar.');
        } finally {
            setSavingSettings(false);
        }
    };

    if (!isAuthenticated) return null;

    return (
        <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="admin-dashboard">
            {/* Top bar */}
            <header className="sticky top-0 z-40 border-b bg-white/80 backdrop-blur-md">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex h-14 items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Link to="/"><img src="/images/asg-logo.png" alt="ASG Motor" className="h-8 w-auto" /></Link>
                            <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Admin</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button onClick={handleSync} disabled={syncing} size="sm" className="bg-[var(--brand-navy)] text-white gap-1.5" data-testid="admin-inventory-sync-button">
                                {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                                {syncing ? 'Sincronizando...' : 'Actualizar'}
                            </Button>
                            <span className="text-xs text-[hsl(var(--muted-foreground))] hidden sm:block">{user?.email}</span>
                            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/admin'); }}><LogOut className="h-4 w-4" /></Button>
                        </div>
                    </div>
                </div>
            </header>

            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                        {[
                            { label: 'Vehículos', value: stats.vehicles?.total || 0, icon: Car },
                            { label: 'Disponibles', value: stats.vehicles?.available || 0, icon: CheckCircle },
                            { label: 'Leads nuevos', value: stats.leads?.new || 0, icon: Users },
                            { label: 'Clicks WA', value: stats.events?.whatsapp_clicks || 0, icon: MessageCircle },
                        ].map((s, i) => (
                            <Card key={i} className="p-3 rounded-xl">
                                <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] mb-1">
                                    <s.icon className="h-3.5 w-3.5" /><span className="text-xs font-medium">{s.label}</span>
                                </div>
                                <p className="font-[var(--font-display)] text-2xl font-bold">{s.value}</p>
                            </Card>
                        ))}
                    </div>
                )}

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-4 flex-wrap">
                        <TabsTrigger value="inventory" data-testid="admin-nav-inventory"><Car className="h-4 w-4 mr-1.5" />Inventario</TabsTrigger>
                        <TabsTrigger value="leads" data-testid="admin-nav-leads"><Users className="h-4 w-4 mr-1.5" />Leads</TabsTrigger>
                        <TabsTrigger value="settings" data-testid="admin-nav-settings"><Settings className="h-4 w-4 mr-1.5" />Ajustes</TabsTrigger>
                        <TabsTrigger value="logs" data-testid="admin-nav-logs"><FileText className="h-4 w-4 mr-1.5" />Sync</TabsTrigger>
                        <TabsTrigger value="security" data-testid="admin-nav-security"><Lock className="h-4 w-4 mr-1.5" />Seguridad</TabsTrigger>
                    </TabsList>

                    {/* ========== INVENTORY TAB ========== */}
                    <TabsContent value="inventory">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="font-[var(--font-display)] font-semibold">Inventario ({vehicles.length})</h2>
                            <Button size="sm" onClick={() => setShowCreate(true)} className="bg-[var(--brand-navy)] text-white gap-1.5">
                                <Plus className="h-4 w-4" /> Añadir vehículo
                            </Button>
                        </div>
                        {loading ? (
                            <Card className="p-4 rounded-xl">{Array.from({length:5}).map((_,i) => <Skeleton key={i} className="h-12 w-full mb-2" />)}</Card>
                        ) : vehicles.length > 0 ? (
                            <Card className="rounded-xl overflow-hidden">
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-14">Foto</TableHead>
                                                <TableHead>Título</TableHead>
                                                <TableHead>Precio</TableHead>
                                                <TableHead>Estado</TableHead>
                                                <TableHead className="text-center">Dest.</TableHead>
                                                <TableHead>Acciones</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {vehicles.map(v => {
                                                const sb = getStatusBadge(v.status);
                                                return (
                                                    <TableRow key={v.id} data-testid="admin-inventory-row">
                                                        <TableCell>
                                                            <div className="h-10 w-14 rounded overflow-hidden bg-[var(--brand-gray-soft)]">
                                                                {v.images?.[0] ? <img src={v.images[0]} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center"><Car className="h-4 w-4 opacity-30" /></div>}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className="text-sm font-medium line-clamp-1">{v.custom_title || v.title}</span>
                                                            <span className="text-xs text-[hsl(var(--muted-foreground))] block">{v.year} | {v.fuel}</span>
                                                        </TableCell>
                                                        <TableCell className="font-medium text-sm">{formatPrice(v.custom_price || v.price)}</TableCell>
                                                        <TableCell>
                                                            <Select value={v.status} onValueChange={(val) => adminUpdateVehicle(v.id, { status: val }).then(() => { toast.success('Estado actualizado'); fetchData(); })}>
                                                                <SelectTrigger className="h-8 w-[120px]"><Badge variant={sb.variant} className="text-xs">{sb.label}</Badge></SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="available">Disponible</SelectItem>
                                                                    <SelectItem value="reserved">Reservado</SelectItem>
                                                                    <SelectItem value="sold">Vendido</SelectItem>
                                                                    <SelectItem value="hidden">Oculto</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </TableCell>
                                                        <TableCell className="text-center">
                                                            <Switch checked={v.featured || false} onCheckedChange={(val) => adminUpdateVehicle(v.id, { featured: val }).then(() => { toast.success(val ? 'Destacado' : 'Quitado de destacados'); fetchData(); })} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex gap-1">
                                                                <Button variant="ghost" size="sm" onClick={() => openEdit(v)} title="Editar"><Pencil className="h-3.5 w-3.5" /></Button>
                                                                <Link to={`/coches/${v.id}`} target="_blank"><Button variant="ghost" size="sm" title="Ver"><Eye className="h-3.5 w-3.5" /></Button></Link>
                                                                <Button variant="ghost" size="sm" onClick={() => handleDelete(v)} title="Eliminar" className="text-red-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></Button>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            </Card>
                        ) : (
                            <Card className="p-8 text-center rounded-xl">
                                <Car className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                <h3 className="font-semibold mb-2">Sin vehículos</h3>
                                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">Pulsa "Actualizar" o "Añadir vehículo".</p>
                            </Card>
                        )}
                    </TabsContent>

                    {/* ========== LEADS TAB ========== */}
                    <TabsContent value="leads">
                        {leads.length > 0 ? (
                            <Card className="rounded-xl overflow-hidden">
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Fecha</TableHead><TableHead>Nombre</TableHead><TableHead>Teléfono</TableHead><TableHead>Email</TableHead><TableHead>Canal</TableHead><TableHead>Mensaje</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {leads.map(l => (
                                            <TableRow key={l.id} data-testid="admin-lead-row">
                                                <TableCell className="text-xs">{l.created_at ? new Date(l.created_at).toLocaleDateString('es-ES') : '-'}</TableCell>
                                                <TableCell className="font-medium text-sm">{l.name || '-'}</TableCell>
                                                <TableCell className="text-sm">{l.phone || '-'}</TableCell>
                                                <TableCell className="text-sm">{l.email || '-'}</TableCell>
                                                <TableCell><Badge variant="secondary" className="text-xs">{l.channel}</Badge></TableCell>
                                                <TableCell className="text-sm max-w-[200px] truncate">{l.message || '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Card>
                        ) : (
                            <Card className="p-8 text-center rounded-xl">
                                <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                <h3 className="font-semibold mb-2">Sin leads</h3>
                                <p className="text-sm text-[hsl(var(--muted-foreground))]">Los mensajes del formulario aparecerán aquí.</p>
                            </Card>
                        )}
                    </TabsContent>

                    {/* ========== SETTINGS TAB ========== */}
                    <TabsContent value="settings">
                        <div className="grid gap-4 md:grid-cols-2">
                            <Card className="p-5 rounded-xl">
                                <h3 className="font-[var(--font-display)] font-semibold text-sm mb-4 flex items-center gap-2"><MessageCircle className="h-4 w-4" /> WhatsApp</h3>
                                <div className="space-y-3">
                                    <div><Label className="text-xs">Número (sin +, ej: 34627947622)</Label>
                                        <Input value={settings.whatsapp_number || ''} onChange={(e) => setSettings(p => ({...p, whatsapp_number: e.target.value}))} className="mt-1" /></div>
                                    <div><Label className="text-xs">Plantilla mensaje</Label>
                                        <Input value={settings.whatsapp_template || ''} onChange={(e) => setSettings(p => ({...p, whatsapp_template: e.target.value}))} className="mt-1" />
                                        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Vars: {'{brand} {model} {year} {km} {price} {url}'}</p></div>
                                    <Button size="sm" onClick={() => { handleSaveSetting('whatsapp_number', settings.whatsapp_number); handleSaveSetting('whatsapp_template', settings.whatsapp_template); }} disabled={savingSettings} className="bg-[var(--brand-navy)] text-white">Guardar</Button>
                                </div>
                            </Card>
                            <Card className="p-5 rounded-xl">
                                <h3 className="font-[var(--font-display)] font-semibold text-sm mb-4 flex items-center gap-2"><Phone className="h-4 w-4" /> Contacto</h3>
                                <div className="space-y-3">
                                    <div><Label className="text-xs">Teléfono</Label><Input value={settings.phone || ''} onChange={(e) => setSettings(p => ({...p, phone: e.target.value}))} className="mt-1" /></div>
                                    <div><Label className="text-xs">Email</Label><Input value={settings.email || ''} onChange={(e) => setSettings(p => ({...p, email: e.target.value}))} className="mt-1" /></div>
                                    <div><Label className="text-xs">Dirección</Label><Input value={settings.address || ''} onChange={(e) => setSettings(p => ({...p, address: e.target.value}))} className="mt-1" /></div>
                                    <div><Label className="text-xs">Horario</Label><Input value={settings.schedule || ''} onChange={(e) => setSettings(p => ({...p, schedule: e.target.value}))} className="mt-1" /></div>
                                    <Button size="sm" onClick={() => { ['phone','email','address','schedule'].forEach(k => handleSaveSetting(k, settings[k])); }} disabled={savingSettings} className="bg-[var(--brand-navy)] text-white">Guardar</Button>
                                </div>
                            </Card>
                            <Card className="p-5 rounded-xl md:col-span-2" data-testid="sync-config-card">
                                <h3 className="font-[var(--font-display)] font-semibold text-sm mb-1 flex items-center gap-2"><Cloud className="h-4 w-4" /> Sincronización con coches.net</h3>
                                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">
                                    Por defecto el scraping se ejecuta <strong>en este mismo servidor</strong>. Si coches.net bloquea su IP,
                                    se usa automáticamente el servidor externo de respaldo configurado abajo.
                                </p>

                                <div className="mb-5 space-y-3 rounded-lg border p-3">
                                    <div>
                                        <Label className="text-xs">Estrategia de sincronización</Label>
                                        <Select value={syncConfig?.strategy || 'local_first'} onValueChange={handleChangeStrategy}>
                                            <SelectTrigger className="mt-1" data-testid="sync-strategy-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="local_first">Este servidor, con respaldo externo (recomendado)</SelectItem>
                                                <SelectItem value="local">Solo este servidor</SelectItem>
                                                <SelectItem value="remote">Solo servidor externo</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Button size="sm" variant="outline" data-testid="scrape-diagnose-btn" onClick={handleDiagnose} disabled={diagnosing}>
                                        {diagnosing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                                        Probar scraping desde este servidor
                                    </Button>
                                    {syncConfig?.last_diagnosis && (
                                        <div
                                            data-testid="scrape-diagnose-result"
                                            className={`rounded-lg p-3 text-xs ${syncConfig.last_diagnosis.ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}
                                        >
                                            {syncConfig.last_diagnosis.ok
                                                ? `coches.net responde correctamente desde este servidor: ${syncConfig.last_diagnosis.items_found} coches detectados (método: ${syncConfig.last_diagnosis.method}).`
                                                : `coches.net no devuelve datos desde este servidor${syncConfig.last_diagnosis.http_status ? ` (HTTP ${syncConfig.last_diagnosis.http_status})` : ''}${syncConfig.last_diagnosis.error ? `: ${syncConfig.last_diagnosis.error}` : ''}.${syncConfig.strategy === 'local_first' && syncConfig.remote_available ? ' Se usará el servidor externo de respaldo.' : ' Configura un servidor externo de respaldo abajo.'}`}
                                            {syncConfig.last_diagnosis.checked_at && (
                                                <span className="block mt-1 opacity-70">Comprobado: {new Date(syncConfig.last_diagnosis.checked_at).toLocaleString('es-ES')}</span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <p className="text-xs font-medium mb-2">Servidor externo de respaldo</p>

                                {syncConfig && (
                                    <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="sync-config-status">
                                        <Badge variant={syncConfig.configured ? 'default' : 'destructive'} className="text-xs">
                                            {syncConfig.configured ? 'Respaldo externo disponible' : 'Sin respaldo externo'}
                                        </Badge>
                                        {syncConfig.has_key && <span className="text-xs text-[hsl(var(--muted-foreground))]">API key: {syncConfig.key_preview}</span>}
                                    </div>
                                )}

                                {syncConfig?.invalid_reason && (
                                    <div className="mb-4 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="sync-config-warning">
                                        <AlertTriangle className="h-4 w-4 shrink-0" />
                                        <span>{syncConfig.invalid_reason}</span>
                                    </div>
                                )}

                                <div className="space-y-3">
                                    <div>
                                        <Label className="text-xs">URL del servidor de scraping</Label>
                                        <Input
                                            data-testid="sync-config-url-input"
                                            value={syncForm.emergent_url}
                                            onChange={(e) => setSyncForm(p => ({ ...p, emergent_url: e.target.value }))}
                                            placeholder="https://xxxx.preview.emergentagent.com"
                                            className="mt-1 font-mono text-xs"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">API key compartida (déjalo vacío para no cambiarla)</Label>
                                        <Input
                                            data-testid="sync-config-key-input"
                                            type="password"
                                            value={syncForm.remote_sync_key}
                                            onChange={(e) => setSyncForm(p => ({ ...p, remote_sync_key: e.target.value }))}
                                            placeholder="••••••••"
                                            className="mt-1 font-mono text-xs"
                                        />
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button size="sm" data-testid="sync-config-save-btn" onClick={handleSaveSyncConfig} disabled={savingSettings} className="bg-[var(--brand-navy)] text-white">
                                            <Save className="h-3.5 w-3.5 mr-1.5" />Guardar
                                        </Button>
                                        <Button size="sm" variant="outline" data-testid="sync-config-test-btn" onClick={handleTestSyncConfig} disabled={testingSync}>
                                            {testingSync ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                                            Probar conexión
                                        </Button>
                                    </div>

                                    {syncTestResult && (
                                        <div
                                            data-testid="sync-config-test-result"
                                            className={`rounded-lg p-3 text-xs ${syncTestResult.ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
                                        >
                                            {syncTestResult.ok
                                                ? `Conexión correcta: ${syncTestResult.count} coches en el servidor de scraping${syncTestResult.last_synced_at ? ` (último scraping: ${new Date(syncTestResult.last_synced_at).toLocaleString('es-ES')})` : ''}.`
                                                : syncTestResult.error}
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* ========== SYNC LOGS TAB ========== */}
                    <TabsContent value="logs">
                        {syncLogs.length > 0 ? (
                            <Card className="rounded-xl overflow-hidden">
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Fecha</TableHead><TableHead>Origen</TableHead><TableHead>Estado</TableHead><TableHead>Encontrados</TableHead><TableHead>Añadidos</TableHead><TableHead>Actualizados</TableHead><TableHead>Ocultos</TableHead><TableHead>Errores</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {syncLogs.map(log => (
                                            <Fragment key={log.id}>
                                            <TableRow data-testid={`sync-log-row-${log.id}`}>
                                                <TableCell className="text-xs">{log.started_at ? new Date(log.started_at).toLocaleString('es-ES') : '-'}</TableCell>
                                                <TableCell className="text-xs">{log.mode?.startsWith('remote') ? 'Externo' : 'Este servidor'}</TableCell>
                                                <TableCell><Badge variant={log.status === 'success' ? 'default' : log.status === 'running' ? 'secondary' : 'destructive'} className="text-xs">{log.status === 'success' ? 'OK' : log.status === 'running' ? 'En curso' : 'Error'}</Badge></TableCell>
                                                <TableCell>{log.total_found || 0}</TableCell>
                                                <TableCell className="text-[var(--brand-success)]">{log.vehicles_added || 0}</TableCell>
                                                <TableCell>{log.vehicles_updated || 0}</TableCell>
                                                <TableCell>{log.vehicles_removed || 0}</TableCell>
                                                <TableCell>{log.errors?.length > 0 ? <Badge variant="destructive" className="text-xs">{log.errors.length}</Badge> : '-'}</TableCell>
                                            </TableRow>
                                            {log.errors?.length > 0 && (
                                                <TableRow data-testid={`sync-log-errors-${log.id}`}>
                                                    <TableCell colSpan={8} className="bg-amber-50 text-xs text-amber-900 py-2">
                                                        <ul className="list-disc pl-5 space-y-0.5">
                                                            {log.errors.map((e, i) => <li key={i}>{e}</li>)}
                                                        </ul>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            </Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Card>
                        ) : (
                            <Card className="p-8 text-center rounded-xl">
                                <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                                <h3 className="font-semibold mb-2">Sin registros</h3>
                                <p className="text-sm text-[hsl(var(--muted-foreground))]">Pulsa "Actualizar" para sincronizar.</p>
                            </Card>
                        )}
                    </TabsContent>

                    {/* ========== SECURITY TAB ========== */}
                    <TabsContent value="security">
                        <div className="max-w-md">
                            <Card className="p-5 rounded-xl">
                                <h3 className="font-[var(--font-display)] font-semibold text-sm mb-4 flex items-center gap-2"><Lock className="h-4 w-4" /> Cambiar contraseña</h3>
                                <div className="space-y-3">
                                    <div><Label className="text-xs">Contraseña actual</Label>
                                        <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm(p => ({...p, current_password: e.target.value}))} className="mt-1" /></div>
                                    <div><Label className="text-xs">Nueva contraseña</Label>
                                        <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm(p => ({...p, new_password: e.target.value}))} className="mt-1" /></div>
                                    <div><Label className="text-xs">Confirmar nueva contraseña</Label>
                                        <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm(p => ({...p, confirm: e.target.value}))} className="mt-1" /></div>
                                    <Button onClick={handleChangePw} disabled={savingPw} className="bg-[var(--brand-navy)] text-white gap-1.5">
                                        <Lock className="h-4 w-4" /> {savingPw ? 'Cambiando...' : 'Cambiar contraseña'}
                                    </Button>
                                </div>
                            </Card>
                            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3">Usuario actual: {user?.email}</p>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* ========== EDIT VEHICLE DIALOG ========== */}
            <Dialog open={!!editVehicle} onOpenChange={(open) => { if (!open) setEditVehicle(null); }}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Editar vehículo</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                        <div><Label className="text-xs">Título</Label><Input value={editForm.title || ''} onChange={(e) => setEditForm(p => ({...p, title: e.target.value}))} className="mt-1" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs">Precio (€)</Label><Input type="number" value={editForm.price || ''} onChange={(e) => setEditForm(p => ({...p, price: e.target.value}))} className="mt-1" /></div>
                            <div><Label className="text-xs">Año</Label><Input type="number" value={editForm.year || ''} onChange={(e) => setEditForm(p => ({...p, year: e.target.value}))} className="mt-1" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs">Kilómetros</Label><Input type="number" value={editForm.km || ''} onChange={(e) => setEditForm(p => ({...p, km: e.target.value}))} className="mt-1" /></div>
                            <div><Label className="text-xs">Potencia (cv)</Label><Input type="number" value={editForm.horsepower || ''} onChange={(e) => setEditForm(p => ({...p, horsepower: e.target.value}))} className="mt-1" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs">Combustible</Label>
                                <Select value={editForm.fuel || ''} onValueChange={(v) => setEditForm(p => ({...p, fuel: v}))}>
                                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Gasolina">Gasolina</SelectItem><SelectItem value="Diésel">Diésel</SelectItem>
                                        <SelectItem value="Híbrido">Híbrido</SelectItem><SelectItem value="Eléctrico">Eléctrico</SelectItem><SelectItem value="GLP">GLP</SelectItem>
                                    </SelectContent>
                                </Select></div>
                            <div><Label className="text-xs">Cambio</Label>
                                <Select value={editForm.transmission || ''} onValueChange={(v) => setEditForm(p => ({...p, transmission: v}))}>
                                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="Manual">Manual</SelectItem><SelectItem value="Automático">Automático</SelectItem></SelectContent>
                                </Select></div>
                        </div>
                        <div><Label className="text-xs">Estado</Label>
                            <Select value={editForm.status || 'available'} onValueChange={(v) => setEditForm(p => ({...p, status: v}))}>
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="available">Disponible</SelectItem><SelectItem value="reserved">Reservado</SelectItem>
                                    <SelectItem value="sold">Vendido</SelectItem><SelectItem value="hidden">Oculto</SelectItem>
                                </SelectContent>
                            </Select></div>
                        <div className="flex items-center gap-2">
                            <Switch checked={editForm.featured || false} onCheckedChange={(v) => setEditForm(p => ({...p, featured: v}))} />
                            <Label className="text-xs">Destacado</Label>
                        </div>
                        <div><Label className="text-xs">Descripción</Label><Textarea value={editForm.description || ''} onChange={(e) => setEditForm(p => ({...p, description: e.target.value}))} className="mt-1 min-h-[80px]" /></div>
                        <div><Label className="text-xs">Fotos</Label><div className="mt-1"><ImageUploader images={editForm.images || []} onChange={(imgs) => setEditForm(p => ({...p, images: imgs}))} /></div></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditVehicle(null)}>Cancelar</Button>
                        <Button onClick={saveEdit} disabled={savingEdit} className="bg-[var(--brand-navy)] text-white gap-1.5"><Save className="h-4 w-4" />{savingEdit ? 'Guardando...' : 'Guardar'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ========== CREATE VEHICLE DIALOG ========== */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Añadir vehículo manual</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                        <div><Label className="text-xs">Título *</Label><Input placeholder="Ej: TOYOTA Rav4 2.5 Advance" value={createForm.title} onChange={(e) => setCreateForm(p => ({...p, title: e.target.value}))} className="mt-1" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs">Marca</Label><Input placeholder="TOYOTA" value={createForm.brand} onChange={(e) => setCreateForm(p => ({...p, brand: e.target.value}))} className="mt-1" /></div>
                            <div><Label className="text-xs">Modelo</Label><Input placeholder="Rav4 2.5 Advance" value={createForm.model} onChange={(e) => setCreateForm(p => ({...p, model: e.target.value}))} className="mt-1" /></div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div><Label className="text-xs">Precio (€)</Label><Input type="number" value={createForm.price} onChange={(e) => setCreateForm(p => ({...p, price: e.target.value}))} className="mt-1" /></div>
                            <div><Label className="text-xs">Año</Label><Input type="number" value={createForm.year} onChange={(e) => setCreateForm(p => ({...p, year: e.target.value}))} className="mt-1" /></div>
                            <div><Label className="text-xs">Km</Label><Input type="number" value={createForm.km} onChange={(e) => setCreateForm(p => ({...p, km: e.target.value}))} className="mt-1" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs">Combustible</Label>
                                <Select value={createForm.fuel} onValueChange={(v) => setCreateForm(p => ({...p, fuel: v}))}>
                                    <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                    <SelectContent><SelectItem value="Gasolina">Gasolina</SelectItem><SelectItem value="Diésel">Diésel</SelectItem><SelectItem value="Híbrido">Híbrido</SelectItem><SelectItem value="Eléctrico">Eléctrico</SelectItem></SelectContent>
                                </Select></div>
                            <div><Label className="text-xs">Cambio</Label>
                                <Select value={createForm.transmission} onValueChange={(v) => setCreateForm(p => ({...p, transmission: v}))}>
                                    <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                    <SelectContent><SelectItem value="Manual">Manual</SelectItem><SelectItem value="Automático">Automático</SelectItem></SelectContent>
                                </Select></div>
                        </div>
                        <div><Label className="text-xs">Descripción</Label><Textarea value={createForm.description} onChange={(e) => setCreateForm(p => ({...p, description: e.target.value}))} className="mt-1" placeholder="Descripción del vehículo..." /></div>
                        <div><Label className="text-xs">Fotos del vehículo</Label><div className="mt-1"><ImageUploader images={createForm.images} onChange={(imgs) => setCreateForm(p => ({...p, images: imgs}))} /></div></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                        <Button onClick={saveCreate} disabled={savingCreate} className="bg-[var(--brand-navy)] text-white gap-1.5"><Plus className="h-4 w-4" />{savingCreate ? 'Creando...' : 'Crear vehículo'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
